import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiIcon,
  MiIconButton,
  MiLoader,
} from "@miplatform/components";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  ButtonVariants,
  Colors,
  IAttempt,
  IStep,
  LoaderType,
  StepDisplayConfigButtonType,
  StepStatus,
  StepType,
} from "@miplatform/types";
import {
  mdiArrowULeftTop,
  mdiContentCopy,
  mdiOpenInNew,
  mdiThumbUpOutline,
} from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { WorkOrderDocumentStep } from "./WorkOrderDocumentStep";
import { useWorkOrderStepCardViewModel } from "./useWorkOrderStepCardViewModel";

export const WorkOrderStepCard = ({
  step,
  number,
  isDisabled,
  isUndoDisabled,
  isLoading: isRefreshing,
  isEditMode,
  attempt,
  onRefresh,
}: WorkOrderStepCardProps) => {
  const wasCompleted = step.status === StepStatus.Completed;
  const isCompleted =
    step.status === StepStatus.Completed ||
    step.stepResult?.status === StepStatus.Completed;

  const { classes } = useStyles({ isCompleted });
  const [isCopied, turnIsCopiedOn] = useBoolean();
  const {
    isLoading: loading,
    submitStepResult,
    undoStepResult,
  } = useWorkOrderStepCardViewModel({
    attempt,
    step,
    onRefresh,
  });
  const isLoading = Boolean(loading || isRefreshing);

  const handleLinkClick = () => {
    if (step.displayConfig.buttonType === StepDisplayConfigButtonType.Link) {
      const normalizeUrl = (url: string) => {
        if (!url) return "";

        if (url.startsWith("http://") || url.startsWith("https://")) return url;

        return `https://${url.replace(/^\/+/, "")}`;
      };

      window.open(
        normalizeUrl(step.displayConfig.content || ""),
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      navigator.clipboard
        .writeText(step.displayConfig.content || "")
        .catch(() =>
          toastService.error({ text: "Failed to copy link to clipboard" })
        );
      turnIsCopiedOn();
    }
  };

  return (
    <Box className={classes.section}>
      <MiLoader
        type={LoaderType.Linear}
        isLoading={loading}
        style={{ position: "absolute", top: 0, left: 0, right: 0 }}
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <Box display="flex" flexDirection="column">
          {step.isRequired && (
            <Typography className={classes.required}>REQUIRED</Typography>
          )}
          <Typography
            className={classes.title}
          >{`${number}. ${step.title}`}</Typography>
          <Typography className={classes.description}>
            {step.description}
          </Typography>
        </Box>
        <Box display="flex" pl={1} gap={1}>
          {step.displayConfig.hasButton && !isCompleted && (
            <MiButton
              title={isCopied ? "Copied" : step.displayConfig.buttonLabel}
              startIcon={{
                path:
                  step.displayConfig.buttonType ===
                  StepDisplayConfigButtonType.Link
                    ? mdiOpenInNew
                    : mdiContentCopy,
              }}
              disabled={isDisabled}
              color={isCopied ? Colors.Success : undefined}
              variant={ButtonVariants.Outlined}
              onClick={handleLinkClick}
            />
          )}
          {isCompleted ? (
            <MiButton variant={ButtonVariants.Text} title="Completed" />
          ) : (
            <MiButton
              title="Complete Step"
              disabled={
                isDisabled ||
                isLoading ||
                (step.stepType === StepType.Document &&
                  (isEditMode
                    ? !step.stepResult?.documents?.length
                    : !step.stepResult?.documents?.length &&
                      !step.documents?.length))
              }
              startIcon={{
                path: mdiThumbUpOutline,
              }}
              variant={ButtonVariants.Outlined}
              onClick={submitStepResult}
            />
          )}
          {isEditMode && (
            <MiIconButton
              disabled={
                isLoading ||
                isDisabled ||
                !isCompleted ||
                isUndoDisabled ||
                wasCompleted
              }
              onClick={undoStepResult}
            >
              <MiIcon path={mdiArrowULeftTop} />
            </MiIconButton>
          )}
        </Box>
      </Box>
      <Box>
        {step.stepType === StepType.Document && (
          <WorkOrderDocumentStep
            step={step}
            isDisabled={isDisabled}
            isEditMode={isEditMode}
            onRefresh={onRefresh}
            attempt={attempt}
          />
        )}
      </Box>
    </Box>
  );
};

interface WorkOrderStepCardProps {
  step: IStep;
  isDisabled?: boolean;
  isUndoDisabled?: boolean;
  isLoading?: boolean;
  number: number;
  attempt: IAttempt;
  isEditMode?: boolean;
  onRefresh: () => void;
}

const useStyles = makeStyles<{ isCompleted: boolean }>()(
  (theme, { isCompleted }) => ({
    section: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      padding: theme.spacing(3),
      width: "100%",
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey.light,
      border: `1px solid ${
        isCompleted ? theme.palette.success.main : theme.palette.grey.accent
      }`,
    },
    required: {
      fontSize: theme.typography.pxToRem(10),
      color: theme.palette.error.main,
    },
    title: {
      fontWeight: theme.fontWeight.semiBold,
    },
    description: {
      color: theme.palette.grey.dark,
      fontSize: theme.typography.pxToRem(14),
    },
  })
);
